import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';

const motionSx = {
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    '&::after': { animation: 'none' },
  },
};

const Bar = ({ width = '100%', height = 14 }) => (
  <Skeleton variant="rounded" animation="wave" width={width} height={height} sx={motionSx} />
);

export const PreviewSkeleton = ({ label = 'Preparing PDF preview...' }) => (
  <Box role="status" aria-label={label} aria-busy="true" sx={{ flex: 1, minHeight: 240, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Stack aria-hidden="true" spacing={2} sx={{ width: 'min(100%, 760px)', aspectRatio: '210 / 297', maxHeight: '100%', p: { xs: 2, sm: 3 }, boxSizing: 'border-box', overflow: 'hidden', backgroundColor: '#fff', borderRadius: 1, boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)' }}>
      <Bar width="70%" height={22} />
      <Bar width="50%" />
      {[0, 1, 2].map(section => (
        <Stack key={section} spacing={1}>
          <Bar width="40%" />
          <Bar />
          <Bar width="85%" />
        </Stack>
      ))}
    </Stack>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

export const ImportSkeleton = ({ label, compact = false }) => (
  <Box role={label ? 'status' : undefined} aria-label={label} aria-busy={label ? true : undefined} sx={{ width: '100%', py: 1 }}>
    {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
    <Stack aria-hidden="true" spacing={1} sx={{ mt: 1 }}>
      <Bar width="70%" />
      {!compact && <Bar />}
      {!compact && <Bar width="85%" />}
    </Stack>
  </Box>
);

export const PhotoSkeleton = () => (
  <Box role="status" aria-label="Processing resume photo" aria-busy="true">
    <Skeleton aria-hidden="true" variant="circular" animation="wave" width={72} height={72} sx={motionSx} />
  </Box>
);

export const ThumbnailSkeleton = () => (
  <Skeleton aria-hidden="true" variant="rectangular" animation="wave" width="100%" height="100%" sx={motionSx} />
);

// This is deliberately independent of builder/PDF modules so it can be part
// of the small application shell while the resume-builder route downloads.
export const ResumeBuilderSkeleton = () => (
  <Box aria-hidden="true" sx={{ mt: 3, textAlign: 'left' }}>
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Bar width="45%" height={32} />
      <Bar width="70%" />
      <Bar width="100%" height={40} />
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, gap: 3 }}>
      <Stack spacing={3} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Bar width="60%" height={24} />
        <Bar height={70} />
        <Bar height={70} />
        <Bar width="85%" />
        <Bar width="70%" />
      </Stack>
      <Stack spacing={2} sx={{ p: 3, borderRadius: 3, backgroundColor: '#e8edf3', minHeight: 320 }}>
        <Bar width="60%" height={22} />
        <Bar height={240} />
      </Stack>
    </Box>
  </Box>
);
